class BuyingGuideCollectionSwitcher {
  constructor(section) {
    this.section = section;
    this.buttons = Array.from(
      section.querySelectorAll('[data-collection-switcher-button]')
    );
    this.containerSelector = '[data-collection-section-id]';
    this.sectionId = null;
    this.activeRequest = null;

    this.handleClick = this.handleClick.bind(this);
    this.handlePopState = this.handlePopState.bind(this);

    this.init();
  }

  init() {
    if (!this.buttons.length) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.handleClick);
    });

    this.resolveTargetSection();
    this.syncActiveState();

    if (!this.section.dataset.collectionSwitcherHistoryBound) {
      window.addEventListener('popstate', this.handlePopState);
      this.section.dataset.collectionSwitcherHistoryBound = 'true';
    }
  }

  resolveTargetSection() {
    this.targetContainer = document.querySelector(this.containerSelector);
    if (!this.targetContainer) return;
    this.sectionId = this.targetContainer.dataset.collectionSectionId;
  }

  handlePopState() {
    if (!this.sectionId) return;

    const segments = window.location.pathname
      .split('/')
      .filter((segment) => segment.length);
    const handleIndex = segments.indexOf('collections') + 1;
    const handle =
      handleIndex > 0 && handleIndex < segments.length
        ? segments[handleIndex]
        : null;

    if (!handle) return;

    const button = this.buttons.find(
      (item) => item.dataset.collectionHandle === handle
    );

    if (button) {
      this.loadCollection(button, { updateHistory: false });
    }
  }

  handleClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (!button || button.classList.contains('is-active')) {
      return;
    }

    if (!this.targetContainer || !document.body.contains(this.targetContainer)) {
      this.resolveTargetSection();
    }

    if (!this.targetContainer || !this.sectionId) {
      console.warn(
        '[CollectionSwitcher] Target collection section not found on the page.'
      );
      return;
    }

    this.loadCollection(button, { updateHistory: true });
  }

  loadCollection(button, { updateHistory }) {
    const targetHandle = button.dataset.collectionHandle;
    if (
      this.targetContainer &&
      this.targetContainer.dataset.collectionHandle === targetHandle &&
      button.classList.contains('is-active')
    ) {
      if (!updateHistory) {
        this.syncActiveState(targetHandle);
      }
      return;
    }

    const collectionUrl = button.dataset.collectionUrl;
    if (!collectionUrl) return;

    this.setLoadingState(button, true);
    const requestUrl = new URL(collectionUrl, window.location.origin);
    requestUrl.searchParams.set('sections', this.sectionId);

    if (this.activeRequest?.abort) {
      this.activeRequest.abort();
    }

    const controller = new AbortController();
    this.activeRequest = controller;

    fetch(requestUrl.toString(), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        return response.json();
      })
      .then((payload) => {
        const html = payload?.[this.sectionId];
        if (!html) {
          throw new Error('Section HTML missing from response.');
        }

        const temp = document.createElement('div');
        temp.innerHTML = html;
        const newContainer =
          temp.querySelector(`[data-collection-section-id="${this.sectionId}"]`) ||
          temp.querySelector(this.containerSelector);

        if (!newContainer) {
          throw new Error('Updated collection container not found.');
        }

        this.targetContainer.replaceWith(newContainer);
        this.targetContainer = newContainer;
        this.sectionId = this.targetContainer.dataset.collectionSectionId;

        if (updateHistory) {
          this.updateHistory(requestUrl);
        }
        this.syncActiveState(this.targetContainer.dataset.collectionHandle);
        this.refreshSectionDependencies();
        this.dispatchSectionLoad();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[CollectionSwitcher] Failed to switch collection:', error);
      })
      .finally(() => {
        this.setLoadingState(button, false);
      });
  }

  setLoadingState(button, isLoading) {
    if (isLoading) {
      this.buttons.forEach((btn) => {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
        btn.disabled = true;
      });
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      this.buttons.forEach((btn) => {
        const isActive = btn.classList.contains('is-active');
        btn.disabled = isActive;
      });
    }
  }

  syncActiveState(activeHandle) {
    if (!activeHandle && this.targetContainer) {
      activeHandle = this.targetContainer.dataset.collectionHandle;
    }

    this.buttons.forEach((button) => {
      const matches = button.dataset.collectionHandle === activeHandle;
      button.classList.toggle('is-active', matches);
      button.setAttribute('aria-pressed', matches ? 'true' : 'false');
      button.disabled = matches;
    });
  }

  updateHistory(url) {
    if (!window.history?.pushState) return;
    if (window.Shopify && Shopify.designMode) return;

    const newUrl = new URL(window.location.href);
    newUrl.pathname = url.pathname;
    newUrl.search = '';
    newUrl.hash = '';

    window.history.pushState({ collectionHandle: url.pathname }, '', newUrl);
  }

  dispatchSectionLoad() {
    if (!this.targetContainer) return;

    const sectionElement =
      this.targetContainer.closest('.shopify-section') || this.targetContainer;
    const eventDetail = {
      sectionId: this.sectionId,
      sectionElement
    };

    sectionElement.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: eventDetail
      })
    );

    document.dispatchEvent(
      new CustomEvent('buying-guide:collection-updated', { detail: eventDetail })
    );
  }

  refreshSectionDependencies() {
    if (!this.targetContainer) return;

    const sectionElement =
      this.targetContainer.closest('.shopify-section') || this.targetContainer;

    const toolbar = sectionElement.querySelector('products-toolbar');
    if (toolbar) {
      const filtersComponent =
        sectionElement.querySelector('facet-filters') ||
        document.querySelector('facet-filters');
      const filtersColumn = sectionElement.querySelector('.main-products-grid__filters');
      const productsGrid = sectionElement.querySelector('.main-products-grid');

      if (filtersComponent) toolbar.filtersComponent = filtersComponent;
      if (filtersColumn) toolbar.filtersColumn = filtersColumn;
      if (productsGrid) toolbar.productsGrid = productsGrid;
    }
  }
}

const initializeCollectionSwitchers = (container = document) => {
  container
    .querySelectorAll('[data-collection-switcher]')
    .forEach((section) => {
      if (section.dataset.collectionSwitcherInitialized === 'true') return;
      section.dataset.collectionSwitcherInitialized = 'true';
      new BuyingGuideCollectionSwitcher(section);
    });
};

document.addEventListener('DOMContentLoaded', () => {
  initializeCollectionSwitchers();
});

document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event?.detail?.sectionId;
  if (!sectionId) return;
  const section = document.querySelector(
    `[data-section-id="${sectionId}"][data-collection-switcher]`
  );
  if (section) {
    initializeCollectionSwitchers(section.parentElement || section);
  }
});


